import { Alert, Box, CircularProgress, Typography } from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  FileDetail,
  FileStage,
  FileWorkflowState,
  WorkflowField,
} from '@munifocus/contracts-gov';
import { AppShell } from '../AppShell';
import { StageLayout } from '../StageLayout';
import type { StageRecordRow } from '../StageRecord';
import { pickLang, useAdvanceWorkflow, useFileWorkflow } from '../../lib/bylaw-workflows-api';
import { WorkflowDeadlinesPanel } from './WorkflowDeadlinesPanel';
import { WorkflowStepCard } from './WorkflowStepCard';

type Translate = ReturnType<typeof useTranslation>['t'];

function display(
  field: WorkflowField,
  value: string | number | boolean | null | undefined,
  lang: string,
  t: Translate,
): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (field.type === 'boolean') {
    return value ? t('gov.bylawWorkflow.stage.yes') : t('gov.bylawWorkflow.stage.no');
  }
  if (field.type === 'select') {
    const option = field.options?.find((o) => o.value === value);
    return option ? pickLang(option.label, lang) : String(value);
  }
  return String(value);
}

/** What a stage recorded, as the read-only record a passed stage shows. */
function recordRows(
  state: FileWorkflowState,
  stageId: FileStage,
  lang: string,
  t: Translate,
): StageRecordRow[] {
  const rows: StageRecordRow[] = [];
  for (const step of state.definition.steps.filter((s) => s.stage === stageId)) {
    const record = state.records[step.key];
    for (const field of step.fields) {
      rows.push({
        label: `${pickLang(step.title, lang)} — ${pickLang(field.label, lang)}`,
        value: display(field, record?.values?.[field.key], lang, t),
        block: field.type === 'textarea',
      });
    }
    for (const item of step.checklist) {
      rows.push({
        label: pickLang(item.label, lang),
        value: record?.checklist?.[item.key]
          ? t('gov.bylawWorkflow.stage.yes')
          : t('gov.bylawWorkflow.stage.no'),
      });
    }
  }
  return rows;
}

/**
 * A stage of a configured by-law workflow — Property Standards, Noise and the
 * rest — rendered entirely from the file's definition. The steps, their fields
 * and checklists, the clocks and the letters are all data the API resolved for
 * the file's municipality; nothing here knows which by-law it is showing.
 *
 * Before escalation the primary action completes the stage, once every step
 * at it is complete. At escalation the officer works whichever steps the case
 * needs, and the file closes on a recorded closing decision.
 */
export function ConfiguredWorkflowStage({
  file,
  stageId,
}: {
  file: FileDetail;
  stageId: FileStage;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const workflow = useFileWorkflow(file.id);
  const advance = useAdvanceWorkflow(file.id);

  if (workflow.isLoading || !workflow.data) {
    return (
      <AppShell back={{ label: file.reference, onClick: () => navigate(`/files/${file.id}`) }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          {workflow.isLoading ? (
            <CircularProgress aria-label={t('gov.bylawWorkflow.stage.loading')} />
          ) : (
            <Alert severity="error">{t('gov.bylawWorkflow.stage.loadError')}</Alert>
          )}
        </Box>
      </AppShell>
    );
  }

  const state = workflow.data;
  const isEscalation = stageId === 'escalation';
  const steps = state.definition.steps.filter((s) => s.stage === stageId);
  const readOnly = file.stage !== stageId;

  const onComplete = () =>
    advance.mutate(
      { expectedStage: file.stage, ...(isEscalation ? { close: true } : {}) },
      {
        onSuccess: (next) =>
          navigate(
            next.stage === 'closed' ? `/files/${file.id}` : `/files/${file.id}/stage/${next.stage}`,
          ),
      },
    );

  return (
    <StageLayout
      file={file}
      stageId={stageId}
      onComplete={onComplete}
      saving={advance.isPending}
      disabled={isEscalation ? !state.canClose : !state.canAdvance}
      completeLabel={isEscalation ? t('gov.bylawWorkflow.stage.closeFile') : undefined}
      record={recordRows(state, stageId, lang, t)}
    >
      <Typography variant="body2" color="text.secondary">
        {pickLang(state.definition.label, lang)}
      </Typography>
      <Alert severity="info">
        {isEscalation
          ? t('gov.bylawWorkflow.stage.escalationHint')
          : t('gov.bylawWorkflow.stage.stageHint')}
      </Alert>

      <WorkflowDeadlinesPanel deadlines={state.deadlines} />

      {steps.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('gov.bylawWorkflow.stage.noSteps')}
        </Typography>
      )}
      {steps.map((step) => (
        <WorkflowStepCard
          key={step.key}
          fileId={file.id}
          definition={state.definition}
          step={step}
          state={state.steps.find((s) => s.key === step.key)}
          record={state.records[step.key]}
          readOnly={readOnly}
        />
      ))}
    </StageLayout>
  );
}
